import React from 'react';
import { useNavigate } from 'react-router-dom';
import AdminButton from './ui/AdminButton';
import AdminCard from './ui/AdminCard';
import { spacing, typography, colors } from './ui/tokens';

/**
 * AdminErrorBoundary
 * 
 * Catches React render errors and displays a friendly fallback UI.
 * Wraps the admin app content to prevent crashes from breaking the entire UI.
 */
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error with requestId if available
    const requestId = error?.requestId || errorInfo?.componentStack?.match(/requestId[:\s]+(\S+)/)?.[1];
    console.error('Admin Error Boundary caught error:', {
      error,
      errorInfo,
      requestId,
      timestamp: new Date().toISOString(),
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error, errorInfo }) {
  const navigate = useNavigate();
  const requestId = error?.requestId || null;

  return (
    <div style={styles.container}>
      <AdminCard title="Something went wrong">
        <div style={styles.content}>
          <p style={styles.message}>
            An unexpected error occurred. Please try reloading the page or returning to the admin home.
          </p>
          
          {requestId && (
            <p style={styles.requestId}>
              Request ID: <code style={styles.code}>{requestId}</code>
            </p>
          )}

          {process.env.NODE_ENV === 'development' && error && (
            <details style={styles.details}>
              <summary style={styles.summary}>Error Details (Development Only)</summary>
              <pre style={styles.pre}>
                {error.toString()}
                {errorInfo?.componentStack && `\n\n${errorInfo.componentStack}`}
              </pre>
            </details>
          )}

          <div style={styles.actions}>
            <AdminButton
              variant="primary"
              onClick={() => navigate('/admin')}
              ariaLabel="Back to Admin Home"
            >
              Back to Admin Home
            </AdminButton>
            <AdminButton
              variant="secondary"
              onClick={() => window.location.reload()}
              ariaLabel="Reload page"
            >
              Reload Page
            </AdminButton>
          </div>
        </div>
      </AdminCard>
    </div>
  );
}

const styles = {
  container: {
    padding: spacing['2xl'],
    maxWidth: '800px',
    margin: '0 auto',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
  },
  message: {
    ...typography.body,
    color: colors.text.primary,
  },
  requestId: {
    ...typography.bodySmall,
    color: colors.text.secondary,
  },
  code: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    backgroundColor: colors.background.tertiary,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
  },
  details: {
    marginTop: spacing.md,
  },
  summary: {
    ...typography.body,
    fontWeight: '500',
    cursor: 'pointer',
    color: colors.text.secondary,
    marginBottom: spacing.sm,
  },
  pre: {
    ...typography.bodySmall,
    fontFamily: 'monospace',
    backgroundColor: colors.background.tertiary,
    padding: spacing.md,
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '400px',
    color: colors.error.base,
  },
  actions: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
};

export default AdminErrorBoundary;

